(() => {
  const STRINGS = Object.freeze({
    mismatch: 'De to e-mailadresser matcher ikke. Kontroller venligst din indtastning.',
    confirmLabel: 'Bekræft e-mail',
    required: 'Bekræft venligst din e-mailadresse.'
  });

  const CLASS_NAMES = Object.freeze({
    wrapper: '_formFieldContainer_1mxsl_5',
    wrapperError: '_withError_1mxsl_17',
    label: '_formInputFieldLabel_1mxsl_38',
    labelFilled: '_inputFilled_1mxsl_74',
    confirm: '_formInputField_nag3b_7',
    confirmError: '_withError_1mxsl_17',
    error: '_formError_1mxsl_21'
  });

  const DATA_FLAGS = Object.freeze({
    confirm: 'emailConfirm',
    primary: 'emailConfirmPrimary'
  });

  const EMAIL_SELECTOR = [
    'input[type="email"]',
    'input[inputmode="email"]',
    'input[name*="email" i]',
    'input[name*="mail" i]',
    'input[id*="email" i]',
    'input[id*="mail" i]',
    'input[data-testid*="email" i]'
  ].join(', ');

  const COPY_PASTE_BLOCKED_EVENTS = Object.freeze([
    'copy',
    'cut',
    'paste',
    'drop',
    'dragover',
    'contextmenu'
  ]);

  const DEBUG = false;
  const debug = DEBUG ? (message, ...args) => console.debug('[email-confirm]', message, ...args) : () => {};

  const processedForms = new WeakSet();
  const observedRoots = new WeakSet();

  const observer = new MutationObserver((mutations) => {
    debug('Mutation detected, rescanning');
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node instanceof HTMLFormElement) {
          processForm(node);
          return;
        }

        if (node instanceof Element && node.shadowRoot) {
          observeRoot(node.shadowRoot);
          scan(node.shadowRoot);
        }

        if (node instanceof Element || node instanceof DocumentFragment) {
          scan(node);
        }
      });
    });
  });

  function blockCopyPaste(input) {
    const stop = (event) => {
      event.preventDefault();
      debug('Blocked', event.type);
    };

    COPY_PASTE_BLOCKED_EVENTS.forEach((eventName) => input.addEventListener(eventName, stop));
    input.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
        stop(event);
      }
    });
  }

  function isValidPrimaryInput(input) {
    if (input.dataset[DATA_FLAGS.confirm] === '1') return false;
    if (input.dataset[DATA_FLAGS.primary] === '1') return false;
    if (input.type?.toLowerCase() === 'password') return false;
    if (input.disabled || input.readOnly) return false;

    const style = window.getComputedStyle(input);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    if (!input.offsetParent && style.position !== 'fixed') return false;

    return true;
  }

  function findPrimaryEmailInput(form) {
    return (
      Array.from(form.querySelectorAll(EMAIL_SELECTOR)).find(
        (input) => input instanceof HTMLInputElement && isValidPrimaryInput(input)
      ) || null
    );
  }

  function createConfirmField(emailInput) {
    if (emailInput.dataset[DATA_FLAGS.primary] === '1') return null;
    emailInput.dataset[DATA_FLAGS.primary] = '1';

    const fieldContainer =
      emailInput.closest('.field, .form-field, .input, .FormField, [class*="_formFieldContainer"]') ||
      emailInput;

    const wrapper = document.createElement('div');
    wrapper.className = CLASS_NAMES.wrapper;

    const confirmId = `${emailInput.id || 'email'}-confirm`;

    const label = document.createElement('label');
    label.id = 'label-email-confirm';
    label.className = CLASS_NAMES.label;
    label.htmlFor = confirmId;
    label.textContent = STRINGS.confirmLabel;
    label.style.top = '16.5px';

    const confirm = document.createElement('input');
    confirm.className = [CLASS_NAMES.confirm, emailInput.className, CLASS_NAMES.confirmError]
      .filter(Boolean)
      .join(' ')
      .trim();
    confirm.type = 'email';
    confirm.id = confirmId;
    confirm.dataset[DATA_FLAGS.confirm] = '1';
    confirm.placeholder = STRINGS.confirmLabel;
    confirm.required = true;
    confirm.autocomplete = 'off';
    confirm.inputMode = 'email';
    confirm.removeAttribute('name');

    const error = document.createElement('output');
    error.className = CLASS_NAMES.error;
    error.setAttribute('role', 'alert');
    error.hidden = true;

    blockCopyPaste(confirm);

    const applyLabelState = (filled) => {
      if (!CLASS_NAMES.labelFilled) return;
      const method = filled ? 'add' : 'remove';
      label.classList[method](CLASS_NAMES.labelFilled);
    };

    const applyErrorState = (message) => {
      const hasMessage = Boolean(message);
      confirm.setCustomValidity(message || '');
      error.textContent = message || '';
      error.hidden = !hasMessage;

      wrapper.className = hasMessage
        ? `${CLASS_NAMES.wrapper} ${CLASS_NAMES.wrapperError}`.trim()
        : CLASS_NAMES.wrapper;
    };

    const validate = () => {
      const primaryValue = (emailInput.value || '').trim();
      const confirmValue = (confirm.value || '').trim();

      applyLabelState(confirmValue.length > 0);

      if (!confirmValue.length) {
        applyErrorState(STRINGS.required);
        return false;
      }

      if (!primaryValue.length || primaryValue !== confirmValue) {
        applyErrorState(STRINGS.mismatch);
        return false;
      }

      applyErrorState('');
      return true;
    };

    emailInput.addEventListener('input', validate);
    confirm.addEventListener('input', validate);
    confirm.addEventListener('blur', validate);

    wrapper.append(confirm, label);
    fieldContainer.insertAdjacentElement('afterend', wrapper);
    wrapper.insertAdjacentElement('afterend', error);

    debug('Inserted confirm field next to', emailInput);

    return {
      confirm,
      label,
      error,
      validate,
      reset() {
        confirm.value = '';
        applyLabelState(false);
        applyErrorState('');
      }
    };
  }

  function handleSubmit(form, helpers) {
    form.addEventListener(
      'submit',
      (event) => {
        if (helpers.validate()) return;

        debug('Prevent submit because emails do not match', form);
        event.preventDefault();
        event.stopImmediatePropagation();
        helpers.confirm.focus();
        helpers.confirm.reportValidity?.();
        form.reportValidity?.();
      },
      true
    );

    form.addEventListener('reset', () => {
      window.requestAnimationFrame(() => helpers.reset());
    });
  }

  function processForm(form) {
    if (processedForms.has(form)) return;

    const emailInput = findPrimaryEmailInput(form);
    if (!emailInput) return;

    const helpers = createConfirmField(emailInput);
    if (!helpers) return;

    handleSubmit(form, helpers);
    processedForms.add(form);

    debug('Wired form', form);
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) return;
    observer.observe(root, { childList: true, subtree: true });
    observedRoots.add(root);
  }

  function walk(root) {
    if (!root) return;

    if (root instanceof HTMLFormElement) {
      processForm(root);
    }

    root.querySelectorAll?.('form')?.forEach(processForm);

    root.querySelectorAll?.('*')?.forEach((element) => {
      if (!element.shadowRoot) return;
      observeRoot(element.shadowRoot);
      walk(element.shadowRoot);
    });
  }

  function scan(root = document) {
    walk(root);
  }

  function init() {
    debug('Script initialised');
    observeRoot(document);
    if (document.documentElement) observeRoot(document.documentElement);
    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  observeRoot(document);
})();
